import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Users, Server, TrendingUp, DollarSign } from 'lucide-react';
import NavigationControls from '../components/NavigationControls';
import PartieBadge from '../components/PartieBadge';
import { useLanguage } from '../contexts/LanguageContext';
import { preSeedInvestment, equipeCommerciale } from '../mock';

export default function OrganisationPage() {
  const { language } = useLanguage();

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto">
        <PartieBadge path="/organisation" />
        <Card className="bg-black/60 border-lime-500/30 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl sm:text-3xl lg:text-4xl text-white mb-2">
              {language === 'fr' ? 'Organisation & Coûts' : 'Organization & Costs'}
            </CardTitle>
            <div className="h-1 w-16 sm:w-24 bg-gradient-to-r from-lime-500 to-cyan-500 rounded-full" />
          </CardHeader>
          <CardContent className="space-y-8">
            {/* === USE OF FUNDS PRÉ-SEED 500K === */}
            <div className="p-6 bg-gradient-to-br from-lime-500/15 via-cyan-500/10 to-transparent rounded-2xl border-2 border-lime-500/40" data-testid="org-use-of-funds">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-lime-500 text-black text-[10px] font-bold rounded-full uppercase tracking-wider">
                  {language === 'fr' ? 'Pré-seed 500K — Utilisation des fonds' : 'Pre-seed $500K — Use of Funds'}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left p-3 text-gray-400 font-semibold">{language === 'fr' ? 'Poste' : 'Item'}</th>
                      <th className="text-right p-3 text-gray-400 font-semibold">{language === 'fr' ? 'Budget 6 mois' : '6-month budget'}</th>
                      <th className="text-right p-3 text-gray-400 font-semibold">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { label: language === 'fr' ? 'GTM direct (3 vendeurs, closer, support, leads)' : 'Direct GTM (3 reps, closer, support, leads)', val: preSeedInvestment.useOfFunds.gtm, color: 'lime' },
                      { label: language === 'fr' ? 'Développement / stabilisation produit' : 'Development / product stabilization', val: preSeedInvestment.useOfFunds.development, color: 'cyan' },
                      { label: language === 'fr' ? 'Ledger / payout rules / MOR logic' : 'Ledger / payout rules / MOR logic', val: preSeedInvestment.useOfFunds.ledger, color: 'orange' },
                      { label: language === 'fr' ? 'Legal / corporate / securities' : 'Legal / corporate / securities', val: preSeedInvestment.useOfFunds.legal, color: 'purple' },
                      { label: language === 'fr' ? 'Marketing / launch assets' : 'Marketing / launch assets', val: preSeedInvestment.useOfFunds.marketing, color: 'cyan' },
                      { label: language === 'fr' ? 'Réserve d\'exécution (imprévus, accélération ON, déplacements)' : 'Execution reserve (contingency, ON acceleration, travel)', val: preSeedInvestment.useOfFunds.reserve, color: 'orange' },
                    ].map((p, i) => (
                      <tr key={i} className="border-b border-gray-800 hover:bg-white/5">
                        <td className="p-3 text-white">{p.label}</td>
                        <td className={`p-3 text-right text-${p.color}-400 font-bold font-mono`}>{p.val.toLocaleString()}$</td>
                        <td className="p-3 text-right text-gray-400">{((p.val / preSeedInvestment.total) * 100).toFixed(0)}%</td>
                      </tr>
                    ))}
                    <tr className="bg-lime-500/10 border-t-2 border-lime-500/40">
                      <td className="p-3 text-lime-400 font-bold uppercase tracking-wider text-xs">{language === 'fr' ? 'Total' : 'Total'}</td>
                      <td className="p-3 text-right text-lime-400 font-bold text-lg font-mono">{preSeedInvestment.total.toLocaleString()}$</td>
                      <td className="p-3 text-right text-lime-400 font-bold">100%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                {language === 'fr'
                  ? 'La réserve couvre les imprévus, l\'accélération Ontario, l\'onboarding additionnel, la préparation seed, l\'accounting et un démo maker additionnel si nécessaire.'
                  : 'The reserve covers contingencies, Ontario acceleration, additional onboarding, seed preparation, accounting, and an additional demo maker if needed.'}
              </p>
            </div>

            {/* === ÉQUIPE COMMERCIALE PRÉ-SEED === */}
            <div className="p-6 bg-gradient-to-br from-cyan-500/15 to-transparent rounded-xl border-2 border-cyan-500/40" data-testid="org-team">
              <h3 className="text-xl font-bold text-cyan-400 mb-4">
                {language === 'fr' ? 'Équipe Commerciale (6 mois)' : 'Commercial Team (6 months)'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { name: language === 'fr' ? `${equipeCommerciale.vendeurs} vendeurs` : `${equipeCommerciale.vendeurs} sales reps`, role: language === 'fr' ? 'Appels, qualification, démos, signatures' : 'Calls, qualification, demos, signatures', color: 'lime' },
                  { name: language === 'fr' ? 'Top Closer Québec' : 'Top Closer Quebec', role: language === 'fr' ? 'Closing post-démo, objections, Programme Fondateur' : 'Post-demo closing, objections, Founder Program', color: 'cyan' },
                  { name: equipeCommerciale.directeurVentes, role: language === 'fr' ? 'Directeur des ventes / responsable des démonstrations' : 'Sales director / demos lead', color: 'orange' },
                  { name: equipeCommerciale.leadProvider, role: language === 'fr' ? 'Lead provider / listes commerciales / priorisation' : 'Lead provider / sales lists / prioritization', color: 'purple' },
                  { name: equipeCommerciale.supportOnboarding, role: language === 'fr' ? 'Support onboarding / téléphone / suivi post-signature' : 'Onboarding support / phone / post-signature follow-up', color: 'lime' },
                ].map((p, i) => (
                  <div key={i} className={`p-3 bg-black/40 rounded-lg border border-${p.color}-500/30`}>
                    <p className={`text-${p.color}-400 font-bold text-sm`}>{p.name}</p>
                    <p className="text-xs text-gray-400 mt-1">{p.role}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-lime-500/10 rounded-lg border border-lime-500/30">
                <p className="text-xs text-gray-300">
                  <strong className="text-lime-400">{language === 'fr' ? 'Cadence commerciale' : 'Commercial cadence'} :</strong>{' '}
                  {language === 'fr'
                    ? `${equipeCommerciale.vendeurs} vendeurs × ${equipeCommerciale.cadenceParVendeur} traiteurs signés/mois = 15 traiteurs/mois = ${equipeCommerciale.totalTraiteurs6Mois}-100 traiteurs sur 6 mois.`
                    : `${equipeCommerciale.vendeurs} reps × ${equipeCommerciale.cadenceParVendeur} caterers/month = 15/month = ${equipeCommerciale.totalTraiteurs6Mois}-100 caterers over 6 months.`}
                </p>
              </div>
            </div>
            {/* Nouvelles Dépenses Détaillées */}
            <div className="p-6 bg-gradient-to-br from-lime-500/15 to-transparent rounded-xl border-2 border-lime-500/40">
              <h3 className="text-2xl font-bold text-lime-400 mb-4">
                {language === 'fr' ? '💰 Structure de Coûts Optimisée' : '💰 Optimized Cost Structure'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Développement */}
                <div className="p-4 bg-black/40 rounded-lg border border-lime-500/30">
                  <h4 className="text-lime-400 font-semibold mb-3">{language === 'fr' ? 'Développement' : 'Development'}</h4>
                  <p className="text-2xl font-bold text-lime-400 mb-2">{language === 'fr' ? 'Doublé chaque année' : 'Doubles each year'}</p>
                  <div className="space-y-1 text-sm mb-3">
                    <div className="flex justify-between">
                      <span className="text-gray-400">{language === 'fr' ? 'Année 1' : 'Year 1'}:</span>
                      <span className="text-lime-300 font-semibold">60k$</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">{language === 'fr' ? 'Année 2' : 'Year 2'}:</span>
                      <span className="text-lime-300 font-semibold">120k$</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">{language === 'fr' ? 'Année 3' : 'Year 3'}:</span>
                      <span className="text-lime-300 font-semibold">240k$</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">{language === 'fr' ? 'Année 4' : 'Year 4'}:</span>
                      <span className="text-lime-300 font-semibold">480k$</span>
                    </div>
                    <div className="flex justify-between border-t border-gray-700 pt-1 mt-1">
                      <span className="text-gray-400">{language === 'fr' ? 'Année 5' : 'Year 5'}:</span>
                      <span className="text-lime-300 font-semibold">960k$</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">{language === 'fr' ? 'Investissement R&D pour fonctionnalités planifiées (CTO = gouvernance/PI/sécurité)' : 'R&D investment for planned functionalities (CTO = governance/IP/security)'}</p>
                </div>

                {/* Staff */}
                <div className="p-4 bg-black/40 rounded-lg border border-cyan-500/30">
                  <h4 className="text-cyan-400 font-semibold mb-3">Staff</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">{language === 'fr' ? 'Année 1' : 'Year 1'}:</span>
                      <span className="text-cyan-300 font-semibold">280k$</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">{language === 'fr' ? 'Année 2' : 'Year 2'}:</span>
                      <span className="text-cyan-300 font-semibold">415k$</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">{language === 'fr' ? 'Année 3' : 'Year 3'}:</span>
                      <span className="text-cyan-300 font-semibold">715k$</span>
                    </div>
                    <div className="flex justify-between border-t border-gray-700 pt-2">
                      <span className="text-gray-400">{language === 'fr' ? 'Années 4-5' : 'Years 4-5'}:</span>
                      <span className="text-cyan-300 font-semibold">1.1-1.3M$</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    {language === 'fr' ? 'Directeur ventes + vendeur terrain arrivent M7' : 'Sales Director + field seller join at M7'}
                  </p>
                </div>

                {/* Sous-traitants */}
                <div className="p-4 bg-black/40 rounded-lg border border-orange-500/30">
                  <h4 className="text-orange-400 font-semibold mb-3">{language === 'fr' ? 'Sous-traitants' : 'Contractors'}</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">2026:</span>
                      <span className="text-orange-300 font-semibold">60k$</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">2027:</span>
                      <span className="text-orange-300 font-semibold">70k$</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">2028:</span>
                      <span className="text-orange-300 font-semibold">100k$</span>
                    </div>
                  </div>
                </div>

                {/* Marketing + CAPEX */}
                <div className="p-4 bg-black/40 rounded-lg border border-purple-500/30">
                  <h4 className="text-purple-400 font-semibold mb-3">{language === 'fr' ? 'Marketing & CAPEX' : 'Marketing & CAPEX'}</h4>
                  <div className="mb-4">
                    <p className="text-xs text-gray-400 mb-2">Marketing:</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">2026:</span>
                        <span className="text-purple-300">15k$</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">2027:</span>
                        <span className="text-purple-300">60k$</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">2028:</span>
                        <span className="text-purple-300">120k$</span>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-gray-700 pt-3">
                    <p className="text-xs text-gray-400 mb-2">CAPEX:</p>
                    <p className="text-sm text-purple-300 font-semibold">20% {language === 'fr' ? 'revenus dès A2' : 'revenue from Y2'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-cyan-400 mb-4">Infrastructure</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-black/40 border-cyan-500/30">
                  <CardContent className="p-4 text-center">
                    <Server className="h-8 w-8 text-cyan-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">{language === 'fr' ? 'Hébergement' : 'Hosting'}</p>
                    <p className="text-xl font-bold text-white mt-2">{language === 'fr' ? 'Québec' : 'Quebec'}</p>
                  </CardContent>
                </Card>
                <Card className="bg-black/40 border-lime-500/30">
                  <CardContent className="p-4 text-center">
                    <DollarSign className="h-8 w-8 text-lime-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">{language === 'fr' ? 'Budget max' : 'Max budget'}</p>
                    <p className="text-xl font-bold text-white mt-2">≤ 3k$/mois</p>
                  </CardContent>
                </Card>
                <Card className="bg-black/40 border-orange-500/30">
                  <CardContent className="p-4 text-center">
                    <TrendingUp className="h-8 w-8 text-orange-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">{language === 'fr' ? 'Scalabilité' : 'Scalability'}</p>
                    <p className="text-xl font-bold text-white mt-2">Cloud-native</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            <div className="p-6 bg-gradient-to-br from-orange-500/10 to-transparent rounded-xl border border-orange-500/30">
              <h3 className="text-xl font-bold text-orange-400 mb-3">Marketing</h3>
              <div className="space-y-2">
                <p className="text-gray-300 flex items-center gap-2">
                  <span className="text-lime-500">✓</span>
                  {language === 'fr' ? 'Intégré aux coûts ventes' : 'Integrated into sales costs'}
                </p>
                <p className="text-gray-300 flex items-center gap-2">
                  <span className="text-cyan-500">✓</span>
                  {language === 'fr' ? 'Pilotage par KPIs' : 'KPI-driven'}
                </p>
                <p className="text-gray-300 flex items-center gap-2">
                  <span className="text-orange-500">✓</span>
                  ROI-driven approach
                </p>
              </div>
            </div>

            <NavigationControls />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
